/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useRef, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'

function sortByPosition(items = []) {
    return [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
}

function rowsSignature(items = []) {
    return JSON.stringify(items)
}

function applyRowsIfChanged(setRows, signatureRef, rows) {
    const nextSignature = rowsSignature(rows)
    if (nextSignature === signatureRef.current) return false
    signatureRef.current = nextSignature
    setRows(rows)
    return true
}

function setRowsAndSignature(setRows, signatureRef, updater) {
    setRows((previous) => {
        const next = updater(previous)
        signatureRef.current = rowsSignature(next)
        return next
    })
}

function setSignatureForEmpty(signatureRef) {
    signatureRef.current = rowsSignature([])
}

export function useProcedures(companyId, userId = null) {
    const [procedures, setProcedures] = useState([])
    const [loading, setLoading] = useState(true)
    const firstLoadRef = useRef(true)
    const lastSignatureRef = useRef('')

    const fetchProcedures = useCallback(async ({ silent = false } = {}) => {
        if (!companyId) {
            firstLoadRef.current = true
            setSignatureForEmpty(lastSignatureRef)
            setProcedures([])
            setLoading(false)
            return
        }

        if (!silent && firstLoadRef.current) setLoading(true)
        // Use the list_procedures RPC (NOT a raw table read): it is ALWAYS scoped
        // to the active firm — even for a super-admin/owner — so on "China"
        // you only see China's procedures. Source of truth = procedure_companies
        // links (origin fallback when unlinked). (Teo 2026-06-03)
        const { data, error } = await kanbify.rpc('list_procedures')

        if (!error) {
            const nextProcedures = data || []
            applyRowsIfChanged(setProcedures, lastSignatureRef, nextProcedures)
        }
        firstLoadRef.current = false
        setLoading(false)
    }, [companyId])

    useEffect(() => {
        fetchProcedures()
    }, [fetchProcedures])

    useEffect(() => {
        if (!companyId) return undefined

        const channel = kanbify
            .channel(`procedures-list:${companyId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'procedures',
                filter: `company_id=eq.${companyId}`,
            }, () => fetchProcedures({ silent: true }))
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
        }
    }, [companyId, fetchProcedures])

    const add = async (payload) => {
        const nextPosition = procedures.length
        const insertPayload = {
            company_id: companyId,
            title: payload.title,
            description: payload.description || null,
            frequency: payload.frequency || 'daily',
            position: payload.position ?? nextPosition,
            ...(payload.created_by || userId ? { created_by: payload.created_by || userId } : {}),
            ...(payload.repeat_interval && payload.repeat_interval > 1 ? { repeat_interval: payload.repeat_interval } : {}),
            ...(payload.repeat_days && payload.repeat_days.length > 0 ? { repeat_days: payload.repeat_days } : {}),
            ...(payload.board_id ? { board_id: payload.board_id } : {}),
            ...(payload.default_assignee_ids ? { default_assignee_ids: payload.default_assignee_ids } : {}),
            // KPI v4 (Teo 2026-05-26): per-field points and/or flat point_value.
            // Validation in ProceduresView guarantees at least one is set.
            ...(payload.kpi_fields ? { kpi_fields: payload.kpi_fields } : {}),
            ...(payload.point_value != null ? { point_value: payload.point_value } : {}),
            ...(payload.completion_bonus != null ? { completion_bonus: payload.completion_bonus } : {}),
        }

        const { data, error } = await kanbify
            .from('procedures')
            .insert([insertPayload])
            .select()
            .single()

        if (error) return { data: null, error }
        if (data) {
            setRowsAndSignature(setProcedures, lastSignatureRef, (previous) => (
                sortByPosition([...previous, data])
            ))
        }
        return { data, error: null }
    }

    const update = async (id, updates) => {
        const { data, error } = await kanbify
            .from('procedures')
            .update(updates)
            .eq('id', id)
            .select()
            .single()

        if (error) return { data: null, error }
        if (data) {
            setRowsAndSignature(setProcedures, lastSignatureRef, (previous) => sortByPosition(previous.map((item) => (
                item.id === id ? { ...item, ...data } : item
            ))))
        }
        return { data, error: null }
    }

    const remove = async (id) => {
        const { error } = await kanbify.from('procedures').delete().eq('id', id)
        if (!error) {
            setRowsAndSignature(setProcedures, lastSignatureRef, (previous) => (
                previous.filter((item) => item.id !== id)
            ))
        }
        return { error }
    }

    return { procedures, loading, refetch: fetchProcedures, add, update, remove }
}

export function useProcedureSteps(procedureId) {
    const [steps, setSteps] = useState([])
    const [loading, setLoading] = useState(true)
    const firstLoadRef = useRef(true)
    const lastSignatureRef = useRef('')

    const fetchSteps = useCallback(async ({ silent = false } = {}) => {
        if (!procedureId) {
            firstLoadRef.current = true
            setSignatureForEmpty(lastSignatureRef)
            setSteps([])
            setLoading(false)
            return
        }

        if (!silent && firstLoadRef.current) setLoading(true)
        const { data, error } = await kanbify
            .from('procedure_steps')
            .select('*')
            .eq('procedure_id', procedureId)
            .order('position')

        if (!error) {
            applyRowsIfChanged(setSteps, lastSignatureRef, sortByPosition(data || []))
        }
        firstLoadRef.current = false
        setLoading(false)
    }, [procedureId])

    useEffect(() => {
        fetchSteps()
    }, [fetchSteps])

    useEffect(() => {
        if (!procedureId) return undefined

        const channel = kanbify
            .channel(`procedure-steps:${procedureId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'procedure_steps',
                filter: `procedure_id=eq.${procedureId}`,
            }, () => fetchSteps({ silent: true }))
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
        }
    }, [procedureId, fetchSteps])

    const addStep = async (payload) => {
        const { data, error } = await kanbify
            .from('procedure_steps')
            .insert([{
                procedure_id: procedureId,
                text: payload.text,
                position: payload.position ?? steps.length,
                point_value: payload.point_value != null ? payload.point_value : 1,
            }])
            .select()
            .single()

        if (error) return { data: null, error }
        if (data) {
            setRowsAndSignature(setSteps, lastSignatureRef, (previous) => sortByPosition([...previous, data]))
        }
        return { data, error: null }
    }

    const updateStep = async (stepId, updates) => {
        const { data, error } = await kanbify
            .from('procedure_steps')
            .update(updates)
            .eq('id', stepId)
            .eq('procedure_id', procedureId)
            .select()
            .single()

        if (error) return { data: null, error }
        if (data) {
            setRowsAndSignature(setSteps, lastSignatureRef, (previous) => sortByPosition(previous.map((step) => (
                step.id === stepId ? { ...step, ...data } : step
            ))))
        }
        return { data, error: null }
    }

    const removeStep = async (stepId) => {
        const { error } = await kanbify
            .from('procedure_steps')
            .delete()
            .eq('id', stepId)
            .eq('procedure_id', procedureId)

        if (!error) {
            setRowsAndSignature(setSteps, lastSignatureRef, (previous) => (
                previous.filter((step) => step.id !== stepId)
            ))
        }
        return { error }
    }

    const reorder = async (orderedStepIds) => {
        if (!Array.isArray(orderedStepIds)) return { error: new Error('Invalid order payload') }

        const updates = orderedStepIds.map((stepId, index) => (
            kanbify
                .from('procedure_steps')
                .update({ position: index })
                .eq('id', stepId)
                .eq('procedure_id', procedureId)
        ))

        const results = await Promise.all(updates)
        const failed = results.find((result) => result.error)
        if (failed?.error) return { error: failed.error }

        setRowsAndSignature(setSteps, lastSignatureRef, (previous) => orderedStepIds
            .map((stepId, index) => {
                const step = previous.find((item) => item.id === stepId)
                return step ? { ...step, position: index } : null
            })
            .filter(Boolean))

        return { error: null }
    }

    return { steps, loading, refetch: fetchSteps, addStep, updateStep, removeStep, reorder }
}

export function useProcedureInstances(procedureId) {
    const [instances, setInstances] = useState([])
    const [loading, setLoading] = useState(false)

    const fetchInstances = useCallback(async () => {
        if (!procedureId) { setInstances([]); return }
        setLoading(true)
        const { data } = await kanbify
            .from('procedure_instances')
            .select('*, cards(title)')
            .eq('procedure_id', procedureId)
            .order('created_at', { ascending: false })
            .limit(20)
        setInstances(data || [])
        setLoading(false)
    }, [procedureId])

    useEffect(() => { fetchInstances() }, [fetchInstances])

    return { instances, loading }
}

// Tracks which procedures have unseen changes for the current user.
// A procedure is "unseen" when its updated_at > the user's last_viewed_at
// (or when the user has never opened it and it was created by someone else).
export function useProcedureViews(companyId, userId) {
    const [unseenIds, setUnseenIds] = useState(new Set())
    const unseenSignatureRef = useRef('')

    const fetchViews = useCallback(async () => {
        if (!companyId || !userId) {
            if (unseenSignatureRef.current !== '[]') {
                unseenSignatureRef.current = '[]'
                setUnseenIds(new Set())
            }
            return
        }

        // Fetch all procedures + the user's view timestamps in one go
        const [{ data: procs }, { data: views }] = await Promise.all([
            kanbify
                .from('procedures')
                .select('id, updated_at, created_by')
                .eq('company_id', companyId),
            kanbify
                .from('procedure_user_views')
                .select('procedure_id, last_viewed_at')
                .eq('user_id', userId),
        ])

        if (!procs) return

        const viewMap = new Map((views || []).map((v) => [v.procedure_id, v.last_viewed_at]))

        const unseen = new Set(
            procs
                .filter((proc) => {
                    const lastViewed = viewMap.get(proc.id)
                    // Never viewed AND created by someone else → unseen
                    if (!lastViewed) return proc.created_by !== userId
                    // Viewed before, but updated since → unseen
                    return new Date(proc.updated_at) > new Date(lastViewed)
                })
                .map((proc) => proc.id)
        )

        const nextSignature = JSON.stringify([...unseen].sort())
        if (nextSignature !== unseenSignatureRef.current) {
            unseenSignatureRef.current = nextSignature
            setUnseenIds(unseen)
        }
    }, [companyId, userId])

    useEffect(() => {
        fetchViews()
    }, [fetchViews])

    // Re-check when any procedure in the company changes
    useEffect(() => {
        if (!companyId || !userId) return undefined

        const channel = kanbify
            .channel(`proc-views:${companyId}:${userId}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'procedures',
                filter: `company_id=eq.${companyId}`,
            }, () => fetchViews())
            .subscribe()

        return () => kanbify.removeChannel(channel)
    }, [companyId, userId, fetchViews])

    const markViewed = useCallback(async (procedureId) => {
        if (!userId || !procedureId) return

        await kanbify
            .from('procedure_user_views')
            .upsert({ user_id: userId, procedure_id: procedureId, last_viewed_at: new Date().toISOString() },
                { onConflict: 'user_id,procedure_id' })

        setUnseenIds((prev) => {
            const next = new Set(prev)
            next.delete(procedureId)
            unseenSignatureRef.current = JSON.stringify([...next].sort())
            return next
        })
    }, [userId])

    return { unseenIds, markViewed, refetch: fetchViews }
}

export function useProcedureAudit(procedureId) {
    const [entries, setEntries] = useState([])
    const [loading, setLoading] = useState(false)
    const firstLoadRef = useRef(true)
    const lastSignatureRef = useRef('')

    const fetchAudit = useCallback(async ({ silent = false } = {}) => {
        if (!procedureId) {
            firstLoadRef.current = true
            setSignatureForEmpty(lastSignatureRef)
            setEntries([])
            return
        }

        if (!silent && firstLoadRef.current) setLoading(true)
        const { data } = await kanbify
            .from('procedure_audit_log')
            .select(`
                id,
                event_type,
                entity_type,
                event_at,
                actor_id,
                payload,
                actor:profiles!procedure_audit_log_actor_id_fkey (
                    id,
                    full_name,
                    email,
                    avatar_url
                )
            `)
            .eq('procedure_id', procedureId)
            .order('event_at', { ascending: false })

        applyRowsIfChanged(setEntries, lastSignatureRef, data || [])
        firstLoadRef.current = false
        setLoading(false)
    }, [procedureId])

    useEffect(() => {
        fetchAudit()
    }, [fetchAudit])

    useEffect(() => {
        if (!procedureId) return undefined

        const channel = kanbify
            .channel(`procedure-audit:${procedureId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'procedure_audit_log',
                filter: `procedure_id=eq.${procedureId}`,
            }, () => fetchAudit({ silent: true }))
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
        }
    }, [procedureId, fetchAudit])

    return { entries, loading, refetch: fetchAudit }
}
